import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { get } from 'lodash';
import { Field } from 'react-final-form';
import { ColumnTypes, HeaderType } from '../Table/types';
import BasicIcon from '../BasicIcon/BasicIcon';
import { TextContainer } from '../SharedTable/styles';
import Checkbox from '../Checkbox/Checkbox';
import Tag from '../Tag/Tag';
import Button from '../Button/Button';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { TableSizesType } from '../ThemeProvider/types';
import TextInput from '../TextInput/TextInput';
import NumericInput from '../NumericInput/NumericInput';
import { EditableColumnTypes, EditableHeaderType } from '../EditTable/types';
import { FormatterType, TABLEGUTTER } from '../SharedTable/types';
import SelectInput from '../SelectInput/SelectInput';
import DatePicker from '../DatePicker/DatePicker';
import AutoComplete from '../AutoComplete/AutoComplete';
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch';
import MultiSelectInput from '../MultiSelectInput/MultiSelectInput';
import FileInput from '../FileInput/FileInput';
import AsyncAutoComplete from '../AsyncAutocomplete/AsyncAutocomplete';

const LINE_HEIGHT = { small: 18, large: 20 };

type UseTableType<T> = {
  headers: HeaderType<T & { collapsedRows?: T[] }>[] | EditableHeaderType<T>[];
  data?: (T & { collapsedRows?: T[] })[];
  hover?: boolean;
  cols?: number;
  disableAllCheck?: boolean;
  size?: TableSizesType;
  dynamicHeight?: boolean;
  testId?: string;
  processedData?: (T & { collapsedRows?: T[] })[];
  fontSize?: 'small' | 'large';
  setDisableAllCheck?: (value: boolean) => void;
};

const useTable = <T,>({
  disableAllCheck,
  data = [],
  size = 'small',
  dynamicHeight = false,
  hover = true,
  cols = 24,
  testId,
  processedData,
  fontSize = 'small',
  setDisableAllCheck,
}: UseTableType<T>) => {
  type HeaderProps = HeaderType<T>;
  type EditableHeaderProps = EditableHeaderType<T>;

  const { actualTheme } = useContext(ThemeContext);
  const rowsContainerRef: React.Ref<HTMLDivElement> = useRef(null);
  const maxLines = useMemo(() => actualTheme?.table?.sizes[size]?.maxLines, [actualTheme, size]);
  const rowProps = useMemo(() => ({ cols, gutter: TABLEGUTTER, hover }), [cols, hover]);

  const truncateText = useCallback(
    (rowsContainer: HTMLDivElement) => {
      const textCells = rowsContainer.querySelectorAll('p');
      const maxHeight = LINE_HEIGHT[fontSize] * maxLines;
      textCells.forEach((cell) => {
        const wordCount = cell.textContent.split(' ').length;
        for (let i = wordCount; i > 1; i -= 1) {
          if (cell.offsetHeight <= maxHeight) break;
          // Reemplaza la última palabra por '...'
          // eslint-disable-next-line no-param-reassign
          cell.textContent = cell.textContent.replace(/\W*\s(\S)*$/g, '...');
        }
      });
    },
    [fontSize, maxLines],
  );

  const renderIconCell = useCallback((header: HeaderProps, row) => {
    const { key, iconConfig, showCondition } = header;
    const { disabledCondition } = iconConfig;
    const variation =
      typeof key === 'string' && iconConfig.variations && iconConfig?.variations[get(row, key)];
    return (
      (!showCondition || showCondition(row)) && (
        <BasicIcon
          name={variation?.name || iconConfig.name}
          color={variation?.color || iconConfig.color}
          size={iconConfig.size}
          disabled={disabledCondition && disabledCondition(row)}
          // eslint-disable-next-line react/jsx-no-bind
          onClick={
            variation?.onClick
              ? () => variation?.onClick(row)
              : iconConfig?.onClick && (() => iconConfig?.onClick(row))
          }
        />
      )
    );
  }, []);

  const formatText = (text: string, formatter: FormatterType, index?: number) => {
    const isFormatterArray = Array.isArray(formatter);
    if (isFormatterArray && index !== undefined)
      return ((formatter as unknown) as ((text: string) => string)[])[index](text);
    return (formatter as (text: string) => string)(text);
  };

  const readText = useCallback((key: string, row, formatter: FormatterType, index?: number) => {
    const text = get(row, key) ?? '';
    return formatter ? formatText(text, formatter, index) : text;
  }, []);

  const renderTextCell = useCallback(
    (header: HeaderProps, row, rowIndex) => {
      const { key, textConfig, showCondition } = header;
      const hasMultipleKeys = Array.isArray(key);
      const separation = textConfig?.separator || ' ';
      const multipleTexts = hasMultipleKeys
        ? (key as string[]).map((k: string, index: number) =>
            readText(k, row, textConfig?.formatter, index),
          )
        : [];
      let isGroupLabel = false;
      let firstText: string;
      if (header.groupLabels) {
        firstText = multipleTexts ? multipleTexts[0] : undefined;
        const isSameText = multipleTexts && multipleTexts.every((text) => text === firstText);
        isGroupLabel =
          isSameText &&
          (firstText?.includes(header?.groupLabels?.singular) ||
            firstText?.includes(header?.groupLabels?.plural));
      }

      const isEmpty = multipleTexts.every((text) => text === '');

      return (
        (!showCondition || showCondition(row)) && (
          <TextContainer
            lineHeight={LINE_HEIGHT[fontSize]}
            data-testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
            color={textConfig?.color}
            weight={textConfig?.weight}
          >
            {hasMultipleKeys && !isGroupLabel
              ? multipleTexts.join(isEmpty ? '' : separation)
              : firstText || readText(key as string, row, textConfig?.formatter)}
          </TextContainer>
        )
      );
    },
    [fontSize, readText, testId],
  );

  const renderCheckBoxCell = useCallback(
    (header: HeaderProps, row, rowIndex) => {
      const { checkboxConfig, showCondition } = header;
      const isDisabled = checkboxConfig.isDisabled && checkboxConfig.isDisabled(row);
      const showCheck = !showCondition || showCondition(row);
      if (disableAllCheck && !(isDisabled || !showCheck)) {
        setDisableAllCheck(false);
      }
      return (
        showCheck && (
          <Checkbox
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
            value={checkboxConfig.isChecked(row)}
            onChange={
              checkboxConfig.onChangeCheck &&
              (() => {
                checkboxConfig.onChangeCheck(row);
              })
            }
            disabled={isDisabled}
          />
        )
      );
    },
    [disableAllCheck, setDisableAllCheck, testId],
  );

  const renderButtonCell = useCallback(
    (header: HeaderProps, row, rowIndex) => {
      const { buttonConfig, showCondition, key } = header;
      const { disabledCondition } = buttonConfig;
      const variation =
        typeof key === 'string' && buttonConfig.variations
          ? buttonConfig?.variations[get(row, key)]
          : buttonConfig;
      return (
        (!showCondition || showCondition(row)) && (
          <Button
            {...variation}
            disabled={disabledCondition && disabledCondition(row)}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => variation.onClick(row)}
            label={buttonConfig.valueLabel ? get(row, key) : buttonConfig.label}
            size="small"
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderTagCell = useCallback(
    (header: HeaderProps, row, rowIndex) => {
      const { tagConfig, key, showCondition } = header;
      const variation = typeof key === 'string' && tagConfig?.variations[get(row, key)];
      return (
        (!showCondition || showCondition(row)) &&
        (variation?.label || variation?.labelKey) && (
          <Tag
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
            label={
              variation?.label || variation?.labelKey
                ? variation?.label || get(row, variation?.labelKey)
                : tagConfig.label || get(row, tagConfig?.labelKey)
            }
            fullWidth={tagConfig?.fullWidth}
            status={variation?.status || tagConfig.status}
          />
        )
      );
    },
    [testId],
  );

  const renderArrayCell = useCallback(
    (header: HeaderProps, row, rowIndex) => {
      const { arrayConfig, showCondition, key } = header;
      const value = get(row, key);
      return (
        (!showCondition || showCondition(row)) && (
          <TextContainer
            lineHeight={LINE_HEIGHT[fontSize]}
            data-testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          >
            {Array.isArray(value) ? arrayConfig.formatter(get(row, key)) : value}
          </TextContainer>
        )
      );
    },
    [fontSize, testId],
  );

  const renderTextInputCell = useCallback(
    (header: EditableHeaderProps, row, rowIndex, name) => {
      const { textInputConfig, key, showCondition } = header;
      return (
        (!showCondition || showCondition(row)) && (
          <Field
            name={`${name}.${key as string}`}
            {...textInputConfig}
            component={TextInput}
            size="small"
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderNumericInputCell = useCallback(
    (header: EditableHeaderProps, row, rowIndex, name) => {
      const { numericInputConfig, key, showCondition } = header;
      return (
        (!showCondition || showCondition(row)) && (
          <Field
            name={`${name}.${key as string}`}
            {...numericInputConfig}
            component={NumericInput}
            size="small"
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderSelectInputCell = useCallback(
    (header: EditableHeaderProps, row, rowIndex, name) => {
      const { selectInputConfig, key, showCondition } = header;
      return (
        (!showCondition || showCondition(row)) && (
          <Field
            name={`${name}.${key as string}`}
            {...selectInputConfig}
            component={SelectInput}
            size="small"
            overlayPosition="bottom"
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderDatePickerCell = useCallback(
    (header: EditableHeaderProps, row, rowIndex, name) => {
      const { datePickerConfig, key, showCondition } = header;
      return (
        (!showCondition || showCondition(row)) && (
          <Field
            name={`${name}.${key as string}`}
            {...datePickerConfig}
            component={DatePicker}
            size="small"
            overlayPosition="bottom"
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderAutoCompleteCell = useCallback(
    (header: EditableHeaderProps, row, rowIndex, name) => {
      const { autoCompleteConfig, key, showCondition } = header;
      return (
        (!showCondition || showCondition(row)) && (
          <Field
            name={`${name}.${key as string}`}
            {...autoCompleteConfig}
            component={AutoComplete}
            size="small"
            overlayPosition="bottom"
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderToggleSwitchCell = useCallback(
    (header: EditableHeaderProps, row, rowIndex, name) => {
      const { toggleSwitchConfig, key, showCondition } = header;
      return (
        (!showCondition || showCondition(row)) && (
          <Field
            name={`${name}.${key as string}`}
            {...toggleSwitchConfig}
            component={ToggleSwitch}
            dataTestId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderMultiSelectInputCell = useCallback(
    (header: EditableHeaderProps, row, rowIndex, name) => {
      const { multiSelectInputConfig, key, showCondition } = header;
      return (
        (!showCondition || showCondition(row)) && (
          <Field
            name={`${name}.${key as string}`}
            {...multiSelectInputConfig}
            component={MultiSelectInput}
            size="small"
            overlayPosition="bottom"
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderFileInputCell = useCallback(
    (header: EditableHeaderProps, row, rowIndex, name) => {
      const { fileInputConfig, key, showCondition } = header;
      return (
        (!showCondition || showCondition(row)) && (
          <Field
            name={`${name}.${key as string}`}
            {...fileInputConfig}
            component={FileInput}
            size="small"
            overlayPosition="bottom"
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderAsyncAutoCompleteCell = useCallback(
    (header: EditableHeaderProps, row, rowIndex, name) => {
      const { asyncAutoCompleteConfig, key, showCondition } = header;
      return (
        (!showCondition || showCondition(row)) && (
          <Field
            name={`${name}.${key as string}`}
            {...asyncAutoCompleteConfig}
            component={AsyncAutoComplete}
            size="small"
            overlayPosition="bottom"
            testId={header.testId && `${testId}-row-${rowIndex}-${header.testId}`}
          />
        )
      );
    },
    [testId],
  );

  const renderCellContent: { [key in ColumnTypes]: Function } = useMemo(
    () => ({
      string: renderTextCell,
      icon: renderIconCell,
      checkbox: renderCheckBoxCell,
      button: renderButtonCell,
      tag: renderTagCell,
      array: renderArrayCell,
    }),
    [
      renderTextCell,
      renderIconCell,
      renderCheckBoxCell,
      renderButtonCell,
      renderTagCell,
      renderArrayCell,
    ],
  );

  const renderEditableCellContent: { [key in EditableColumnTypes]: Function } = useMemo(
    () => ({
      string: renderTextCell,
      icon: renderIconCell,
      tag: renderTagCell,
      array: renderArrayCell,
      textInput: renderTextInputCell,
      numericInput: renderNumericInputCell,
      selectInput: renderSelectInputCell,
      datePicker: renderDatePickerCell,
      autoComplete: renderAutoCompleteCell,
      toggleSwitch: renderToggleSwitchCell,
      multiSelectInput: renderMultiSelectInputCell,
      fileInput: renderFileInputCell,
      asyncAutocomplete: renderAsyncAutoCompleteCell,
    }),
    [
      renderTextCell,
      renderIconCell,
      renderTagCell,
      renderArrayCell,
      renderTextInputCell,
      renderNumericInputCell,
      renderSelectInputCell,
      renderDatePickerCell,
      renderAutoCompleteCell,
      renderToggleSwitchCell,
      renderMultiSelectInputCell,
      renderFileInputCell,
      renderAsyncAutoCompleteCell,
    ],
  );

  useEffect(() => {
    const rowsContainer = rowsContainerRef?.current;
    if (rowsContainer) {
      if (size !== 'fullHeight' || !dynamicHeight) truncateText(rowsContainer);
    }
  }, [rowsContainerRef, truncateText, data, size, dynamicHeight, processedData]);

  useEffect(() => {
    setDisableAllCheck(true);
  }, [data, setDisableAllCheck]);

  return {
    rowProps,
    renderCellContent,
    rowsContainerRef,
    renderEditableCellContent,
  };
};

export default useTable;
